/**
 * AGB = Allgemeine Geschäftsbedingungen (Terms and Conditions)
 *
 * This module handles the latest AGB version consent logic,
 * including grace periods and user acceptance tracking.
 *
 */

import { createGlobalState, useLocalStorage } from '@vueuse/core';
import { ref, Ref, watch } from 'vue';
import {
  getDocumentPdf,
  getConsentStatus,
  getLatestConsentDocumentVersion,
  storeConsentStatusEntry,
} from '@/services/AgbService';
import useUser from '@/mixins/useUser';
import { getUserType } from '@/utils/user';
import { UserType } from '@/models/User';
import { ConsentStatusType } from '@/models/ConsentStatusType';

const useAgbState = createGlobalState(() => {
  const isAgbPopupVisible = ref(false);
  const isPastGracePeriod = ref(false);
  const latestAgbVersion = ref<string | null>(null);
  const shouldShowAgbPopupResult: Ref<boolean | null> = ref(null);
  const waitingForServerResponse = ref(false);

  const remindLaterTimestamp = useLocalStorage<string | null>('agb-remind-later-timestamp', null);

  const { user } = useUser('self');

  const shouldShowAgbPopupInternal = async () => {
    try {
      if (getUserType(user.value) === UserType.GUEST) {
        return false;
      }

      const latestAgbVersion = await getLatestConsentDocumentVersion(ConsentStatusType.LEGAL_TERMS);

      const termsType = user.value?.isCustomer ? ConsentStatusType.LEGAL_TERMS_COMPANY : ConsentStatusType.LEGAL_TERMS;

      const consentStatus = await getConsentStatus(termsType);

      // If response does not contain a timestamp, it means the user was never shown the latest AGB version
      if (!consentStatus.timestamp) {
        return true;
      }

      const hasAcceptedLatestTerms = consentStatus.status === 'ACCEPTED' && consentStatus.object.version === latestAgbVersion;

      if (hasAcceptedLatestTerms) {
        return false;
      }

      const consentStatusTimestampMs = new Date(consentStatus.timestamp).getTime();
      const gracePeriodMs = consentStatus.gracePeriod * 24 * 60 * 60 * 1000;
      const nowMs = Date.now();

      isPastGracePeriod.value = consentStatusTimestampMs + gracePeriodMs < nowMs;

      if (!isPastGracePeriod.value) {
        // User has never clicked "remind me later" before, so we show the popup
        if (!remindLaterTimestamp.value) {
          return true;
        }

        // Only show popup if 24 hours have passed since the user clicked "remind me later"
        const remindLaterTime = new Date(remindLaterTimestamp.value).getTime();
        const twentyFourHoursAgo = Date.now() - 24 * 60 * 60 * 1000;

        return remindLaterTime < twentyFourHoursAgo;
      }

      // User has not accepted the latest terms and is past the grace period, so we show the popup
      return true;
    } catch (error) {
      console.error('Error trying to determine if AGB popup should be shown', error);
      return false;
    }
  };

  const shouldShowAgbPopup = async () => {
    if (isAgbPopupVisible.value) {
      return false;
    }

    shouldShowAgbPopupResult.value = await shouldShowAgbPopupInternal();

    return shouldShowAgbPopupResult.value;
  };

  const showAgbPopup = async (): Promise<void> => {
    if (shouldShowAgbPopupResult.value === null) {
      await shouldShowAgbPopup();
    }

    if (shouldShowAgbPopupResult.value === false) {
      return Promise.resolve();
    }

    isAgbPopupVisible.value = true;

    return new Promise<void>((resolve) => {
      const stopWatching = watch(
        isAgbPopupVisible,
        (visible) => {
          if (!visible) {
            resolve();
            stopWatching();
          }
        },
        { immediate: false }
      );
    });
  };

  const handleRemindLater = async () => {
    if (!latestAgbVersion.value) {
      latestAgbVersion.value = await getLatestConsentDocumentVersion(ConsentStatusType.LEGAL_TERMS);
    }

    try {
      await storeConsentStatusEntry(ConsentStatusType.LEGAL_TERMS, latestAgbVersion.value, 'REQUESTED');
    } catch (error) {
      console.error('Error storing consent status', error);
    }

    remindLaterTimestamp.value = new Date().toISOString();
    isAgbPopupVisible.value = false;
  };

  const handleAcceptAgb = async () => {
    try {
      if (!latestAgbVersion.value) {
        latestAgbVersion.value = await getLatestConsentDocumentVersion(ConsentStatusType.LEGAL_TERMS);
      }

      waitingForServerResponse.value = true;
      await storeConsentStatusEntry(ConsentStatusType.LEGAL_TERMS, latestAgbVersion.value, 'ACCEPTED');
      remindLaterTimestamp.value = null;
    } finally {
      waitingForServerResponse.value = false;
    }
    isAgbPopupVisible.value = false;
  };

  const handleDownloadAgbAsPdf = async () => {
    if (!latestAgbVersion.value) {
      latestAgbVersion.value = await getLatestConsentDocumentVersion(ConsentStatusType.LEGAL_TERMS);
    }

    const agbPdf = await getDocumentPdf(ConsentStatusType.LEGAL_TERMS, latestAgbVersion.value);

    const blobUrl = URL.createObjectURL(agbPdf);
    const link = document.createElement('a');
    link.href = blobUrl;
    link.download = `AGB_v${latestAgbVersion.value}.pdf`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(blobUrl);
  };

  return {
    handleAcceptAgb,
    handleDownloadAgbAsPdf,
    handleRemindLater,
    isAgbPopupVisible,
    isPastGracePeriod,
    shouldShowAgbPopup,
    showAgbPopup,
    waitingForServerResponse,
  };
});

export default useAgbState;
